/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {CreateGeofenceCard, CreateGeofenceCardContextValue} from './CreateGeofenceCard';
import {GeofenceRadius} from './GeofenceRadiusControl';

export type GeofenceSearchCreateCardProps = {
  createCard: CreateGeofenceCardContextValue;
  search: ReactNode;
  isRadial: boolean;
  radius: number;
  sliderProps: Pick<GeofenceRadius, 'max' | 'step' | 'marks'>;
  isCreateDisabled: boolean;
  onCreate: () => void;
  onRadiusChange?: (meters: number) => void;
};

/**
 * Renders GeofenceSearch's location step as the current theme's create
 * geofence card. Like the legacy panel, the card's radius is entered and
 * shown in meters - the same unit the drawn radius is held in.
 *
 * ```typescript
 *
 *   import {GeofenceSearchCreateCard} from
 *     '@ion/dls/src/maps/geofences/GeofenceSearchCreateCard';
 *   ...
 *
 *   <GeofenceSearchCreateCard createCard={createCard}
 *     search={<SearchAddress mapView={view} hideLabel />}
 *     isRadial
 *     radius={1000}
 *     sliderProps={sliderProps}
 *     isCreateDisabled={!drawingComplete}
 *     onCreate={onCreate}
 *     onRadiusChange={onRadiusChange} />
 *
 * ```
 *
 * @param   createCard       The create card state from CreateGeofences.
 * @param   search           The address search field.
 * @param   isRadial         `true` to show the radius control.
 * @param   radius           The drawn radius, in meters.
 * @param   sliderProps      The radius slider's range and marks.
 * @param   isCreateDisabled Disables the Create button when `true`.
 * @param   onCreate         Invoked by the Create button.
 * @param   onRadiusChange   Invoked with the new radius, in meters.
 * @returns                  The create geofence card.
 */
export const GeofenceSearchCreateCard = ({
  createCard,
  search,
  isRadial,
  radius,
  sliderProps,
  isCreateDisabled,
  onCreate,
  onRadiusChange
}: GeofenceSearchCreateCardProps) => {
  const onRadiusSlide = function(meters: number) {
    onRadiusChange?.(meters);
  };

  const onRadiusInput: GeofenceRadius['onInputChange'] = function({target: {value}}) {
    onRadiusChange?.(parseInt(value) || 0);
  };

  return (
    <CreateGeofenceCard selectedGeofenceType={createCard.selectedGeofenceType}
      onChangeGeofenceType={createCard.onChangeGeofenceType}
      onClose={createCard.onClose}
      onCreate={onCreate}
      isCreateDisabled={isCreateDisabled}
      search={search}
      radius={isRadial ? {
        value         : radius,
        max           : sliderProps.max,
        step          : sliderProps.step,
        marks         : sliderProps.marks,
        unitLabel     : translate('maps:geofences.meters'),
        onSlide       : onRadiusSlide,
        onInputChange : onRadiusInput
      } : null} />
  );
};

export default GeofenceSearchCreateCard;
